"use client";

import {
  datePickerFormatJP,
  DEFAULT_CHECKIN_DATE,
  DEFAULT_CHECKOUT_DATE,
  DEFAULT_ADULTS,
} from "@/constants";
import { TRIPLA_BOOKING_URL } from "@/constants/enviroment";
import { cn } from "@/lib/utils";
import { ERoomOrderBy, ERoomType } from "@/types/roomsList";
import {
  groupJapanAreas,
  redirectBookingCard,
  cityAndHotelFuzzyFilter,
  formatDateForStorage,
} from "@/utils";
import { getSearchRoomData } from "@/components/organisms/hotel-result/filter/utils";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { useValidatedDates } from "@/hooks/useValidatedDates";
import { Search } from "lucide-react";
import * as React from "react";
import { useState } from "react";
import { DateRange } from "react-day-picker";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import {
  CustomSearchAvailableProps,
  schemaSearchAvailableRoom,
  SearchAvailableRoom as SearchAvailableRoomType,
} from "./SearchAvailableRoomMobile";
import { LOCALE_BASE_MAP } from "@/i18n/routing";
import useJapanAreasStore from "@/store/hooks/useGlobal";
import { HotelOrCityType } from "./SearchAvailableRoomForm";
import { useIsMobile } from "@/hooks/useIsMobile/useIsMobile";
import useSearchFormStore from "@/store/searchFormStore/searchFormStore";
import { JapanHotel } from "@/types/generated";
import { format } from "date-fns/format";
import { useParams } from "next/navigation";
import { createBookingParams } from "./ultils";
import { Button } from "@/components/atoms/button";
import { DateRangePicker } from "@/components/atoms/date-range-picker";
import { Link } from "@/components/atoms/link/Link";
import { Icon } from "@/components/atoms/icon";
import { CustomSelect } from "@/components/atoms/dropdown";

const SearchHotelMobile: React.FC<
  Omit<CustomSearchAvailableProps, "listOption"> & {
    isTopPage?: boolean;
    hotelOrCity?: HotelOrCityType | null;
    isMobile?: boolean;
  }
> = ({ className, locale, hotelOrCity, ...props }) => {
  const isMobile = useIsMobile();
  const [showValidationError, setShowValidationError] = useState(false);
  const { japanAreas, loading, allLocaleHotels } = useJapanAreasStore();
  const {
    configurations: searchAvailableRoom,
    formData,
    setFormData,
  } = useSearchFormStore();
  const params = useParams();

  const { checkIn: validatedCheckIn, checkOut: validatedCheckOut } = useValidatedDates(
    formData?.checkIn,
    formData?.checkout
  );
  const searchRoomData = getSearchRoomData(searchAvailableRoom, params);
  const { configurations } = searchRoomData || {};
  const { checkin_label, checkout_label } = configurations ?? {};
  const [range, setRange] = useState<DateRange>({
    from: validatedCheckIn,
    to: validatedCheckOut,
  });

  const initialFormData = React.useMemo(() => {
    return {
      hotelOrCity: formData?.hotelOrCity || hotelOrCity,
      checkIn: validatedCheckIn,
      checkout: validatedCheckOut,
    };
  }, [formData, hotelOrCity, validatedCheckIn, validatedCheckOut]);

  const methods = useForm<SearchAvailableRoomType>({
    defaultValues: initialFormData,
    resolver: valibotResolver(schemaSearchAvailableRoom),
  });

  // Sync form values with latest initialFormData (from storage) whenever it changes
  React.useEffect(() => {
    methods.reset(initialFormData);
  }, [initialFormData]);

  const { handleSubmit, control, formState, setValue, reset, getValues } =
    methods;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");
  const [previousRange, setPreviousRange] = useState<DateRange>({
    from: new Date(DEFAULT_CHECKIN_DATE),
    to: new Date(DEFAULT_CHECKOUT_DATE),
  });

  const onSubmit: SubmitHandler<SearchAvailableRoomType> = async (
    values: SearchAvailableRoomType,
  ) => {
    setShowValidationError(false);
    if (!values.hotelOrCity) {
      setShowValidationError(true);
      return;
    }

    const completeFormData: SearchAvailableRoomType = {
      hotelOrCity: values.hotelOrCity,
      checkIn: values.checkIn,
      checkout: values.checkout,
      rooms:
        formData?.rooms && formData?.rooms?.length > 0
          ? formData.rooms
          : [{ adults: Number(DEFAULT_ADULTS), children: [] }],
      promoCode: formData?.promoCode || "",
      preferences: formData?.preferences || [],
      brands: formData?.brands || [],
    };

    if (globalThis.window !== undefined) {
      setFormData({
        ...completeFormData,
        checkIn: formatDateForStorage(values.checkIn as Date, DEFAULT_CHECKIN_DATE),
        checkout: formatDateForStorage(values.checkout as Date, DEFAULT_CHECKOUT_DATE),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
    }

    if (values.hotelOrCity?.type === "city") {
      const slug_hotel =
        (japanAreas || [])?.find(
          (item) =>
            item?.area_name?.toLowerCase() ===
            values?.hotelOrCity?.label.toLowerCase(),
        )?.slug_hotel || "";
      const cityListRoute = `${globalThis.location.origin}/${locale}/${slug_hotel}`;
      window.open(cityListRoute, isMobile ? "_self" : "_blank");
    } else if (values.hotelOrCity?.useTripla === false) {
      window.open(
        values.hotelOrCity?.slug || values.hotelOrCity?.externalUrl,
        isMobile ? "_self" : "_blank",
      );
    } else {
      const response = await fetch('/api/tripla/access-token');
      const tokenData = await response?.json() ?? {};
      const oneTimeToken = tokenData?.one_time_access_token ?? '';
      const queryParams = createBookingParams(completeFormData, {
        type: ERoomType.ROOMS,
        is_day_use: false,
        order: ERoomOrderBy.RECOMMENDED,
        is_including_occupied: false,
        locale: LOCALE_BASE_MAP[locale],
        coupon_code: formData?.promoCode || "",
        ...(oneTimeToken && { one_time_access_token: oneTimeToken }),
      });

      if (TRIPLA_BOOKING_URL?.length && queryParams?.code?.length) {
        redirectBookingCard({
          ...queryParams,
          checkin: queryParams?.checkin || DEFAULT_CHECKIN_DATE,
          checkout: queryParams?.checkout || DEFAULT_CHECKOUT_DATE,
          children: queryParams?.children,
          kids_ages: queryParams?.kids_ages,
        });
      }
    }
  };
  // Update range whenever validatedCheckIn or validatedCheckOut changes (e.g., after loading from local storage)
  // Initialize range from formData on mount only
  React.useEffect(() => {
    if (validatedCheckIn && validatedCheckOut) {
      setRange({
        from: validatedCheckIn,
        to: validatedCheckOut,
      });
    }
  }, [validatedCheckIn, validatedCheckOut]); // Run only once on mount
  const listCityAndHotel = groupJapanAreas(
    japanAreas,
    allLocaleHotels as Record<string, JapanHotel[]> | null | undefined,
    undefined,
    "エリア",
    "ホテル",
  );

  // Reserve space early to prevent CLS before isMobile check
  if (!isMobile) {
    return <div className="md:hidden" style={{ minHeight: '284px' }} />;
  }
  const handleRangeSelect = (
    nextRange: DateRange | undefined,
    selectedDay: Date | undefined,
  ) => {
    // Save previous range for cancel functionality
    if (range.from && range.to) {
      setPreviousRange({ from: range.from, to: range.to });
    }

    if (nextRange) {
      // If user already has a complete range (both from and to), reset and start new selection
      if (range.from && range.to && selectedDay) {
        setRange({ from: selectedDay });
        setValue("checkIn", selectedDay);
        setValue("checkout", null);
      } else {
        // Normal range selection behavior
        setRange(nextRange);
        

        // Update form values
        if (nextRange.from) {
          setValue("checkIn", nextRange.from);
        }
        if (nextRange.to) {
          setValue("checkout", nextRange.to);
        }

        // Save to store only when both dates are selected
        if (nextRange.from && nextRange.to) {
          setFormData({
            ...formData,
            checkIn: formatDateForStorage(nextRange.from, DEFAULT_CHECKIN_DATE),
            checkout: formatDateForStorage(nextRange.to, DEFAULT_CHECKOUT_DATE),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any);
        }
      }
    } else if (!selectedDay) {
      // Reset to defaults if no range
      setRange({
        from: validatedCheckIn,
        to: validatedCheckOut,
      });
    }
  };

  return (
    <div className="flex w-full flex-col gap-6 bg-[var(--fill-base-main)] pt-8" style={{ minHeight: '284px' }}>
      <div
        className={cn("flex w-full flex-col gap-6 px-4 py-6", className)}
        {...props}
      >
        <FormProvider {...methods}>
          <form
            className="flex w-full flex-col gap-3"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="relative flex flex-col gap-1">
              <div className="text-sm font-medium text-white">
                エリア名／ホテル名
              </div>
              <Controller
                name="hotelOrCity"
                control={control}
                render={({ field }) => (
                  <div
                    className={
                      field.value?.type === "hotel" && !isMenuOpen ? "mb-3" : ""
                    }
                  >
                    <CustomSelect
                      inputId="search-hotel-mobile"
                      value={field.value}
                      options={listCityAndHotel}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                        if (showValidationError) {
                          setShowValidationError(false);
                        }
                      }}
                      onFocus={() => {
                        if (showValidationError) {
                          setShowValidationError(false);
                        }
                      }}
                      onMenuOpen={() =>
                        setTimeout(() => setIsMenuOpen(true), 0)
                      }
                      onMenuClose={() =>
                        setTimeout(() => setIsMenuOpen(false), 0)
                      }
                      placeholder={"エリア名／ホテル名を入力してください"}
                      onInputChange={setInputValue}
                      inputValue={inputValue}
                      showAllButton={inputValue?.length > 0}
                      onShowAll={() => {
                        setInputValue("");
                      }}
                      error={formState.errors.hotelOrCity?.value?.message}
                      className="w-full text-sm"
                      iconCustom={
                        <Icon className="scale-80" icon="location-pin" />
                      }
                      isLoading={loading}
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: "8px",
                          padding: "0 16px",
                          height: "44px",
                          "&:hover": {
                            borderColor:
                              showValidationError && !field.value
                                ? "var(--fill-red-solid-normal)"
                                : base.borderColor,
                          },
                          borderColor:
                            showValidationError && !field.value
                              ? "var(--fill-red-solid-normal)"
                              : base.borderColor,
                          borderWidth:
                            showValidationError && !field.value ? "2px" : "1px",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          opacity: 0.6,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontSize: "12px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                        }),
                        option: (base) => ({
                          ...base,
                        }),
                      }}
                      filterOption={(option, inputValue) => {
                        return cityAndHotelFuzzyFilter(
                          { label: option.label, data: option.data },
                          inputValue,
                        );
                      }}
                    />

                    {field.value?.type === "hotel" && !isMenuOpen && (
                      <div className="absolute top-15 z-10 w-full rounded-b-md border border-gray-300 bg-white p-1.5 shadow-md">
                        <Link
                          href={`${globalThis.location.origin}/${locale}/${field.value.slug}`}
                          target="_parent"
                          className="flex items-center justify-center text-xs font-semibold text-[#FFB100] hover:underline"
                        >
                          このホテルの写真・詳細を見る
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              />
            </div>

            <div className="flex flex-col gap-1">
              <div className="text-sm font-medium text-white">
                {`${checkin_label || "チェックイン"}・${checkout_label || "チェックアウト"}`}
              </div>
              <DateRangePicker
                range={range}
                onChange={handleRangeSelect}
                onCancel={() => {
                  setRange(previousRange);
                  setFormData({
                    ...formData,
                    checkIn: formatDateForStorage(previousRange.from, DEFAULT_CHECKIN_DATE),
                    checkout: formatDateForStorage(previousRange.to, DEFAULT_CHECKOUT_DATE),
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  } as any);

                }}
              >
                <div role="button" className="flex w-full items-center rounded-[var(--radius-input-md)] bg-white px-4 py-3 text-sm font-normal">
                  <span className="w-43">
                    {range?.from ? format(range.from, datePickerFormatJP) : ""}
                  </span>
                  <span className="w-14 text-center">→</span>
                  <span className="w-43">
                    {range?.to ? format(range.to, datePickerFormatJP) : ""}
                  </span>
                </div>
              </DateRangePicker>
            </div>

            <Button
              variant="secondary"
              type="submit"
              className="mt-1 flex w-full items-center justify-center gap-2 bg-[var(--fill-secondary-solid-normal)] py-5 font-medium"
            >
              <Search size={20} />
              <span className="text-sm"> 検索</span>
            </Button>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export { SearchHotelMobile };
